import { ButtonGroup, Button, Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem } from '@mui/material';
import React from 'react';
import { CurrentUser, DataRoom, DealPartnerContact, UnlockUser } from '../../../shared/models';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { GetUnlockUserFromDealPartnerContact } from '../../../shared/utils/entity';

interface DataRoomPartnerContactActionButtonProps {
	dataRoom: DataRoom;
	contact: DealPartnerContact;
	currentUser: CurrentUser | null;
	unlockUser: (unlockUser: UnlockUser) => void;
	removeAccess: (contact: DealPartnerContact) => void;
	sendAccountInfo: (contact: DealPartnerContact) => void;
}

type PartnerContactActionItem = keyof typeof PartnerContactActionItemMap;
const PartnerContactActionItemMap = {
	unlockUser: 'Unlock User',
	removeAccess: 'Remove Access',
	SendAccountInfo: 'Send Account Info',
};

export const DataRoomPartnerContactActionButton: React.FC<DataRoomPartnerContactActionButtonProps> = ({
	dataRoom,
	contact,
	currentUser,
	unlockUser,
	removeAccess,
	sendAccountInfo,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const isRoomOpen = dataRoom.roomStatus == 'Open';

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: PartnerContactActionItem) => {
		const userDto = GetUnlockUserFromDealPartnerContact(contact);

		switch (action) {
			case 'unlockUser':
				unlockUser(userDto ?? ({} as UnlockUser));
				break;
			case 'removeAccess':
				removeAccess(contact);
				break;
			case 'SendAccountInfo':
				sendAccountInfo(contact);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}
		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: PartnerContactActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem>
									{contact.isLockedOut && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'unlockUser')}>
											{PartnerContactActionItemMap['unlockUser']}
										</MenuItem>
									)}

									{!contact.isLockedOut && (
										<>
											{currentUser?.isEmployeeUser && (
												<MenuItem onClick={event => handleMenuItemClick(event, 'removeAccess')}>
													{PartnerContactActionItemMap['removeAccess']}
												</MenuItem>
											)}

											{currentUser?.isEmployeeUser && isRoomOpen && (
												<MenuItem
													onClick={event => handleMenuItemClick(event, 'SendAccountInfo')}
												>
													{PartnerContactActionItemMap['SendAccountInfo']}
												</MenuItem>
											)}
										</>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
